import React, { useEffect, useState } from 'react';
import { SaveIcon, DownloadIcon, PrinterIcon, SettingsIcon, HelpCircleIcon, MenuIcon, XIcon, ChevronsRightIcon, UsersIcon, SunIcon, MoonIcon, SearchIcon, UserIcon, BellIcon, GlobeIcon, PaletteIcon, KeyIcon, ShieldIcon, ZapIcon, MessageSquareIcon, RulerIcon, MoreHorizontalIcon, SendIcon, PhoneIcon, InstagramIcon, FacebookIcon, TwitterIcon, LinkedinIcon, MailIcon, ShareIcon, LayersIcon, RefreshCwIcon } from 'lucide-react';
interface TopMenuProps {
  onAction: (action: string, data?: any) => void;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
}
export function TopMenu({
  onAction,
  isDarkMode = false,
  onToggleDarkMode
}: TopMenuProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [showFullMenu, setShowFullMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showRecentCustomers, setShowRecentCustomers] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showNotesMenu, setShowNotesMenu] = useState(false);
  const [showMeasurementsMenu, setShowMeasurementsMenu] = useState(false);
  const [showElementsMenu, setShowElementsMenu] = useState(false);
  const [showLightingMenu, setShowLightingMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showMoreExportOptions, setShowMoreExportOptions] = useState(false);
  const [includeCompanyLogo, setIncludeCompanyLogo] = useState(true);
  const [includeCustomerDetails, setIncludeCustomerDetails] = useState(true);
  const [customerNotesEnabled, setCustomerNotesEnabled] = useState(true);
  const [productionNotesEnabled, setProductionNotesEnabled] = useState(false);
  const [elementNotesEnabled, setElementNotesEnabled] = useState(false);
  const [unitsMeasurementsEnabled, setUnitsMeasurementsEnabled] = useState(true);
  const [frontsMeasurementsEnabled, setFrontsMeasurementsEnabled] = useState(false);
  const [roomMeasurementsEnabled, setRoomMeasurementsEnabled] = useState(false);
  const [frontsEnabled, setFrontsEnabled] = useState(true);
  const [accessoriesEnabled, setAccessoriesEnabled] = useState(false);
  const [outdoorLightingEnabled, setOutdoorLightingEnabled] = useState(false);
  const [indoorLightingEnabled, setIndoorLightingEnabled] = useState(false);
  const [cabinetLightingEnabled, setCabinetLightingEnabled] = useState(false);
  const [fixtureLightingEnabled, setFixtureLightingEnabled] = useState(false);
  const [upperCabinetsOnly, setUpperCabinetsOnly] = useState(false);
  const [outdoorColorTemp, setOutdoorColorTemp] = useState(50);
  const [outdoorIntensity, setOutdoorIntensity] = useState(50);
  const [sunPosition, setSunPosition] = useState(180);
  const [sunHeight, setSunHeight] = useState(45);
  const [indoorColorTemp, setIndoorColorTemp] = useState(50);
  const [indoorIntensity, setIndoorIntensity] = useState(50);
  const [indoorShadowsEnabled, setIndoorShadowsEnabled] = useState(false);
  const [fixtureColorTemp, setFixtureColorTemp] = useState(50);
  const [fixtureIntensity, setFixtureIntensity] = useState(50);
  const [cabinetColorTemp, setCabinetColorTemp] = useState(50);
  const [cabinetIntensity, setCabinetIntensity] = useState(50);
  const [cabinetLightColor, setCabinetLightColor] = useState('white');
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState('#FFFFFF');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  // Reset lighting to default values
  const resetLightingToDefaults = () => {
    setOutdoorLightingEnabled(false);
    setOutdoorColorTemp(50);
    setOutdoorIntensity(50);
    setSunPosition(180);
    setSunHeight(45);
    setIndoorLightingEnabled(false);
    setIndoorColorTemp(50);
    setIndoorIntensity(50);
    setIndoorShadowsEnabled(false);
    setFixtureLightingEnabled(false);
    setFixtureColorTemp(50);
    setFixtureIntensity(50);
    setCabinetLightingEnabled(false);
    setCabinetColorTemp(50);
    setCabinetIntensity(50);
    setUpperCabinetsOnly(false);
    setCabinetLightColor('white');
    setShowCustomColorPicker(false);
    setCustomColor('#FFFFFF');
  };
  // Updated recent customers data to be in English
  const recentCustomers = [{
    id: 1,
    name: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1 (555) 123-4567',
    project: 'Modern Kitchen',
    planNumber: 'K-2023-105',
    lastEdited: '15.11.2023'
  }, {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 234-5678',
    project: 'Rustic Kitchen',
    planNumber: 'K-2023-098',
    lastEdited: '03.11.2023'
  }, {
    id: 3,
    name: 'David Williams',
    email: 'david.w@example.com',
    phone: '+1 (555) 345-6789',
    project: 'Classic Kitchen',
    planNumber: 'K-2023-089',
    lastEdited: '28.10.2023'
  }, {
    id: 4,
    name: 'Michelle Brown',
    email: 'michelle.b@example.com',
    phone: '+1 (555) 456-7890',
    project: 'Minimalist Kitchen',
    planNumber: 'K-2023-075',
    lastEdited: '15.10.2023'
  }, {
    id: 5,
    name: 'Robert Taylor',
    email: 'robert.t@example.com',
    phone: '+1 (555) 567-8901',
    project: 'Industrial Kitchen',
    planNumber: 'K-2023-062',
    lastEdited: '02.10.2023'
  }];
  // Export options for the dropdown
  const exportOptions = [{
    id: 'download',
    icon: DownloadIcon,
    label: 'Download'
  }, {
    id: 'whatsapp',
    icon: PhoneIcon,
    label: 'WhatsApp'
  }, {
    id: 'email',
    icon: MailIcon,
    label: 'Email'
  }, {
    id: 'instagram',
    icon: InstagramIcon,
    label: 'Instagram'
  }, {
    id: 'facebook',
    icon: FacebookIcon,
    label: 'Facebook'
  }];
  // Additional export options shown when clicking "More..."
  const moreExportOptions = [{
    id: 'twitter',
    icon: TwitterIcon,
    label: 'Twitter'
  }, {
    id: 'linkedin',
    icon: LinkedinIcon,
    label: 'LinkedIn'
  }, {
    id: 'share',
    icon: ShareIcon,
    label: 'Share Link'
  }, {
    id: 'print',
    icon: PrinterIcon,
    label: 'Print'
  }, {
    id: 'save',
    icon: SaveIcon,
    label: 'Save As'
  }];
  // Settings menu options
  const settingsOptions = [{
    id: 'appearance',
    name: 'Appearance',
    icon: PaletteIcon,
    description: 'Change theme and UI preferences'
  }, {
    id: 'language',
    name: 'Language',
    icon: GlobeIcon,
    description: 'Change application language'
  }, {
    id: 'notifications',
    name: 'Notifications',
    icon: BellIcon,
    description: 'Manage notification preferences'
  }, {
    id: 'security',
    name: 'Security',
    icon: ShieldIcon,
    description: 'Security settings and permissions'
  }, {
    id: 'shortcuts',
    name: 'Keyboard Shortcuts',
    icon: KeyIcon,
    description: 'View and customize shortcuts'
  }, {
    id: 'performance',
    name: 'Performance',
    icon: ZapIcon,
    description: 'Optimize application performance'
  }];
  // Filter customers based on search query
  const filteredCustomers = recentCustomers.filter(customer => customer.name.toLowerCase().includes(customerSearchQuery.toLowerCase()) || customer.email.toLowerCase().includes(customerSearchQuery.toLowerCase()) || customer.phone.includes(customerSearchQuery));
  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };
  const handleAction = (action: string) => {
    onAction(action);
    if (showMobileMenu) {
      setShowMobileMenu(false);
    }
    if (showSettingsMenu) {
      setShowSettingsMenu(false);
    }
  };
  const handleExportAction = (optionId: string) => {
    onAction('export', {
      method: optionId,
      includeCompanyLogo,
      includeCustomerDetails
    });
    setShowExportMenu(false);
  };
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSettingsMenu || showRecentCustomers || showNotesMenu || showMeasurementsMenu || showExportMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.settings-menu') && !target.closest('.settings-button') && !target.closest('.customers-menu') && !target.closest('.customers-button') && !target.closest('.notes-menu') && !target.closest('.notes-button') && !target.closest('.measurements-menu') && !target.closest('.measurements-button') && !target.closest('.export-menu') && !target.closest('.export-button')) {
          setShowSettingsMenu(false);
          setShowRecentCustomers(false);
          setShowNotesMenu(false);
          setShowMeasurementsMenu(false);
          setShowExportMenu(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsMenu, showRecentCustomers, showNotesMenu, showMeasurementsMenu, showExportMenu]);
  return <div className="bg-white border-b border-gray-200 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1133">
      {/* Desktop menu */}
      <div className="hidden md:block" data-id="element-1134">
        {/* Primary menu row */}
        <div className="flex items-center justify-between px-4 h-12" data-id="element-1135">
          <div className="flex items-center space-x-4" data-id="element-1136">
            <div className="relative" data-id="element-1137">
              {showRecentCustomers && <div className="customers-menu absolute left-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1138">
                  <div className="p-2 border-b border-gray-200 dark:border-darkbg-border" data-id="element-1139">
                    <div className="relative" data-id="element-1140">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1141">
                        <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-1142" />
                      </div>
                      <input type="text" value={customerSearchQuery} onChange={e => setCustomerSearchQuery(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2 dark:bg-darkbg-primary dark:border-darkbg-border dark:text-white" placeholder="Search by name, email, phone..." data-id="element-1143" />
                    </div>
                  </div>
                  <div className="py-1 max-h-80 overflow-y-auto" data-id="element-1144">
                    {filteredCustomers.length > 0 ? filteredCustomers.map(customer => <button key={customer.id} onClick={() => {
                  handleAction('loadCustomer', customer);
                  setShowRecentCustomers(false);
                  setCustomerSearchQuery('');
                }} className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1145">
                          <div className="font-medium" data-id="element-1146">{customer.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1147">
                            {customer.email}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1148">
                            {customer.phone}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1149">
                            {customer.project}
                          </div>
                          <div className="flex justify-between items-center mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1150">
                            <span data-id="element-1151">Plan: {customer.planNumber}</span>
                            <span data-id="element-1152">Edited: {customer.lastEdited}</span>
                          </div>
                        </button>) : <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center" data-id="element-1153">
                        No customers found matching your search.
                      </div>}
                  </div>
                </div>}
            </div>
            {/* Notes Button with Dropdown */}
            <div className="relative" data-id="element-1154">
              <button onClick={() => setShowNotesMenu(!showNotesMenu)} className="notes-button flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1155">
                <MessageSquareIcon className="h-4 w-4 mr-1.5" data-id="element-1156" />
                <span data-id="element-1157">Notes</span>
              </button>
              {showNotesMenu && <div className="notes-menu absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1158">
                  <div className="p-2 border-b border-gray-200 dark:border-darkbg-border" data-id="element-1159">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1160">
                      Toggle Notes Visibility
                    </h3>
                  </div>
                  <div className="py-2" data-id="element-1161">
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1162">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1163">
                        Customer Notes
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1164">
                        <input type="checkbox" id="toggle-customer-notes" checked={customerNotesEnabled} onChange={() => setCustomerNotesEnabled(!customerNotesEnabled)} className="sr-only" data-id="element-1165" />
                        <label htmlFor="toggle-customer-notes" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${customerNotesEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1166">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${customerNotesEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1167"></span>
                        </label>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1168">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1169">
                        Production Notes
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1170">
                        <input type="checkbox" id="toggle-production-notes" checked={productionNotesEnabled} onChange={() => setProductionNotesEnabled(!productionNotesEnabled)} className="sr-only" data-id="element-1171" />
                        <label htmlFor="toggle-production-notes" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${productionNotesEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1172">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${productionNotesEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1173"></span>
                        </label>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1174">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1175">
                        Element Notes
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1176">
                        <input type="checkbox" id="toggle-element-notes" checked={elementNotesEnabled} onChange={() => setElementNotesEnabled(!elementNotesEnabled)} className="sr-only" data-id="element-1177" />
                        <label htmlFor="toggle-element-notes" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${elementNotesEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1178">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${elementNotesEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1179"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Measurements Button with Dropdown - NEW */}
            <div className="relative" data-id="element-1180">
              <button onClick={() => setShowMeasurementsMenu(!showMeasurementsMenu)} className="measurements-button flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1181">
                <RulerIcon className="h-4 w-4 mr-1.5" data-id="element-1182" />
                <span data-id="element-1183">Measurements</span>
              </button>
              {showMeasurementsMenu && <div className="measurements-menu absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1184">
                  <div className="p-2 border-b border-gray-200 dark:border-darkbg-border" data-id="element-1185">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1186">
                      Select Measurement Type
                    </h3>
                  </div>
                  <div className="py-2" data-id="element-1187">
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1188">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1189">
                        Units
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1190">
                        <input type="checkbox" id="toggle-units-measurements" checked={unitsMeasurementsEnabled} onChange={() => setUnitsMeasurementsEnabled(!unitsMeasurementsEnabled)} className="sr-only" data-id="element-1191" />
                        <label htmlFor="toggle-units-measurements" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${unitsMeasurementsEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1192">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${unitsMeasurementsEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1193"></span>
                        </label>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1194">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1195">
                        Fronts
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1196">
                        <input type="checkbox" id="toggle-fronts-measurements" checked={frontsMeasurementsEnabled} onChange={() => setFrontsMeasurementsEnabled(!frontsMeasurementsEnabled)} className="sr-only" data-id="element-1197" />
                        <label htmlFor="toggle-fronts-measurements" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${frontsMeasurementsEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1198">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${frontsMeasurementsEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1199"></span>
                        </label>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1200">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1201">
                        Room
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1202">
                        <input type="checkbox" id="toggle-room-measurements" checked={roomMeasurementsEnabled} onChange={() => setRoomMeasurementsEnabled(!roomMeasurementsEnabled)} className="sr-only" data-id="element-1203" />
                        <label htmlFor="toggle-room-measurements" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${roomMeasurementsEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1204">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${roomMeasurementsEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1205"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Elements Button with Dropdown - NEW */}
            <div className="relative" data-id="element-1206">
              <button onClick={() => setShowElementsMenu(!showElementsMenu)} className="elements-button flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1207">
                <LayersIcon className="h-4 w-4 mr-1.5" data-id="element-1208" />
                <span data-id="element-1209">Elements</span>
              </button>
              {showElementsMenu && <div className="elements-menu absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1210">
                  <div className="p-2 border-b border-gray-200 dark:border-darkbg-border" data-id="element-1211">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1212">
                      Toggle Elements Visibility
                    </h3>
                  </div>
                  <div className="py-2" data-id="element-1213">
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1214">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1215">
                        Fronts
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1216">
                        <input type="checkbox" id="toggle-fronts" checked={frontsEnabled} onChange={() => setFrontsEnabled(!frontsEnabled)} className="sr-only" data-id="element-1217" />
                        <label htmlFor="toggle-fronts" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${frontsEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1218">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${frontsEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1219"></span>
                        </label>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex items-center justify-between" data-id="element-1220">
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1221">
                        Accessories
                      </span>
                      <div className="relative inline-block w-10 align-middle select-none" data-id="element-1222">
                        <input type="checkbox" id="toggle-accessories" checked={accessoriesEnabled} onChange={() => setAccessoriesEnabled(!accessoriesEnabled)} className="sr-only" data-id="element-1223" />
                        <label htmlFor="toggle-accessories" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${accessoriesEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1224">
                          <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${accessoriesEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1225"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Lighting Button with Dropdown - NEW */}
            <div className="relative" data-id="element-1226">
              <button onClick={() => setShowLightingMenu(!showLightingMenu)} className="lighting-button flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1227">
                <SunIcon className="h-4 w-4 mr-1.5" data-id="element-1228" />
                <span data-id="element-1229">Lighting</span>
              </button>
              {showLightingMenu && <div className="lighting-menu absolute left-0 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-50 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1230">
                  <div className="p-2 border-b border-gray-200 dark:border-darkbg-border" data-id="element-1231">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1232">
                      Lighting Controls
                    </h3>
                  </div>
                  <div className="py-2" data-id="element-1233">
                    {/* Outdoor Lighting Section */}
                    <div className="px-4 py-2" data-id="element-1234">
                      <div className="flex items-center justify-between mb-2" data-id="element-1235">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1236">
                          Outdoor Lighting
                        </span>
                        <div className="relative inline-block w-10 align-middle select-none" data-id="element-1237">
                          <input type="checkbox" id="toggle-outdoor-lighting" checked={outdoorLightingEnabled} onChange={() => setOutdoorLightingEnabled(!outdoorLightingEnabled)} className="sr-only" data-id="element-1238" />
                          <label htmlFor="toggle-outdoor-lighting" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${outdoorLightingEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1239">
                            <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${outdoorLightingEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1240"></span>
                          </label>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-1241">
                        <div data-id="element-1242">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1243">
                            Sun Temperature
                          </label>
                          <div className="flex items-center" data-id="element-1244">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1245">
                              Sunrise
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1246">
                              <input type="range" min="0" max="100" value={outdoorColorTemp} onChange={e => setOutdoorColorTemp(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1247" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1248">
                              Sunset
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1249">
                              {outdoorColorTemp}%
                            </span>
                          </div>
                        </div>
                        <div data-id="element-1250">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1251">
                            Sun Intensity
                          </label>
                          <div className="flex items-center" data-id="element-1252">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1253">
                              Low
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1254">
                              <input type="range" min="0" max="100" value={outdoorIntensity} onChange={e => setOutdoorIntensity(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1255" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1256">
                              High
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1257">
                              {outdoorIntensity}%
                            </span>
                          </div>
                        </div>
                        <div data-id="element-1258">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1259">
                            Sun Position
                          </label>
                          <div className="flex items-center" data-id="element-1260">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1261">
                              E
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1262">
                              <input type="range" min="0" max="360" value={sunPosition} onChange={e => setSunPosition(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1263" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1264">
                              W
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1265">
                              {sunPosition}°
                            </span>
                          </div>
                        </div>
                        {/* Add Sun Height control */}
                        <div data-id="element-1266">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1267">
                            Sun Height
                          </label>
                          <div className="flex items-center" data-id="element-1268">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1269">
                              Low
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1270">
                              <input type="range" min="0" max="90" value={sunHeight} onChange={e => setSunHeight(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1271" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1272">
                              High
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1273">
                              {sunHeight}°
                            </span>
                          </div>
                        </div>
                        {/* Add Day/Night toggle below Sun Height */}
                        <div className="flex items-center justify-between mt-2" data-id="element-1274">
                          <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-1275">
                            Night Mode
                          </span>
                          <div className="relative inline-block w-10 align-middle select-none" data-id="element-1276">
                            <input type="checkbox" id="toggle-night-mode" className="sr-only" data-id="element-1277" />
                            <label htmlFor="toggle-night-mode" className="block overflow-hidden h-5 rounded-full cursor-pointer bg-gray-300 dark:bg-gray-600" data-id="element-1278">
                              <span className="block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in translate-x-0" data-id="element-1279"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Indoor Lighting Section - RENAMED TO ROOM LIGHTING */}
                    <div className="px-4 py-2 border-t border-gray-200 dark:border-darkbg-border mt-2" data-id="element-1280">
                      <div className="flex items-center justify-between mb-2" data-id="element-1281">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1282">
                          Room Lighting
                        </span>
                        <div className="relative inline-block w-10 align-middle select-none" data-id="element-1283">
                          <input type="checkbox" id="toggle-indoor-lighting" checked={indoorLightingEnabled} onChange={() => setIndoorLightingEnabled(!indoorLightingEnabled)} className="sr-only" data-id="element-1284" />
                          <label htmlFor="toggle-indoor-lighting" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${indoorLightingEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1285">
                            <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${indoorLightingEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1286"></span>
                          </label>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-1287">
                        <div data-id="element-1288">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1289">
                            Color Temperature
                          </label>
                          <div className="flex items-center" data-id="element-1290">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1291">
                              Cool
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1292">
                              <input type="range" min="0" max="100" value={indoorColorTemp} onChange={e => setIndoorColorTemp(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1293" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1294">
                              Warm
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1295">
                              {indoorColorTemp}%
                            </span>
                          </div>
                        </div>
                        <div data-id="element-1296">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1297">
                            Intensity
                          </label>
                          <div className="flex items-center" data-id="element-1298">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1299">
                              Low
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1300">
                              <input type="range" min="0" max="100" value={indoorIntensity} onChange={e => setIndoorIntensity(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1301" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1302">
                              High
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1303">
                              {indoorIntensity}%
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-1304">
                          <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-1305">
                            Enable Shadows
                          </span>
                          <div className="relative inline-block w-10 align-middle select-none" data-id="element-1306">
                            <input type="checkbox" id="toggle-indoor-shadows" checked={indoorShadowsEnabled} onChange={() => setIndoorShadowsEnabled(!indoorShadowsEnabled)} className="sr-only" data-id="element-1307" />
                            <label htmlFor="toggle-indoor-shadows" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${indoorShadowsEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1308">
                              <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${indoorShadowsEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1309"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* NEW Fixture Lighting Section */}
                    <div className="px-4 py-2 border-t border-gray-200 dark:border-darkbg-border mt-2" data-id="element-1310">
                      <div className="flex items-center justify-between mb-2" data-id="element-1311">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1312">
                          Fixture Lighting
                        </span>
                        <div className="relative inline-block w-10 align-middle select-none" data-id="element-1313">
                          <input type="checkbox" id="toggle-fixture-lighting" checked={fixtureLightingEnabled} onChange={() => setFixtureLightingEnabled(!fixtureLightingEnabled)} className="sr-only" data-id="element-1314" />
                          <label htmlFor="toggle-fixture-lighting" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${fixtureLightingEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1315">
                            <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${fixtureLightingEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1316"></span>
                          </label>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-1317">
                        <div data-id="element-1318">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1319">
                            Color Temperature
                          </label>
                          <div className="flex items-center" data-id="element-1320">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1321">
                              Cool
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1322">
                              <input type="range" min="0" max="100" value={fixtureColorTemp} onChange={e => setFixtureColorTemp(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1323" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1324">
                              Sunset
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1325">
                              {fixtureColorTemp}%
                            </span>
                          </div>
                        </div>
                        <div data-id="element-1326">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1327">
                            Intensity
                          </label>
                          <div className="flex items-center" data-id="element-1328">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1329">
                              Low
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1330">
                              <input type="range" min="0" max="100" value={fixtureIntensity} onChange={e => setFixtureIntensity(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1331" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1332">
                              High
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1333">
                              {fixtureIntensity}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Cabinet Lighting Section */}
                    <div className="px-4 py-2 border-t border-gray-200 dark:border-darkbg-border mt-2" data-id="element-1334">
                      <div className="flex items-center justify-between mb-2" data-id="element-1335">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1336">
                          Cabinet Lighting
                        </span>
                        <div className="relative inline-block w-10 align-middle select-none" data-id="element-1337">
                          <input type="checkbox" id="toggle-cabinet-lighting" checked={cabinetLightingEnabled} onChange={() => setCabinetLightingEnabled(!cabinetLightingEnabled)} className="sr-only" data-id="element-1338" />
                          <label htmlFor="toggle-cabinet-lighting" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${cabinetLightingEnabled ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1339">
                            <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${cabinetLightingEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1340"></span>
                          </label>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-1341">
                        <div data-id="element-1342">
                          <div className="flex items-center mb-1" data-id="element-1343">
                            <label className="block text-xs text-gray-500 dark:text-gray-400" data-id="element-1344">
                              Light Color
                            </label>
                            {/* Color preview dot */}
                            <div className="ml-2 h-3 w-3 rounded-full shadow-inner" style={{
                          backgroundColor: cabinetLightColor === 'custom' ? customColor : cabinetLightColor === 'white' ? '#FFFFFF' : cabinetLightColor === 'neutral' ? '#F5F5DC' : cabinetLightColor === 'warm' ? '#FFD700' : cabinetLightColor === 'amber' ? '#FFBF00' : cabinetLightColor === 'blue' ? '#87CEEB' : '#FFFFFF',
                          boxShadow: `0 0 5px 1px ${cabinetLightColor === 'custom' ? customColor : cabinetLightColor === 'white' ? '#FFFFFF' : cabinetLightColor === 'neutral' ? '#F5F5DC' : cabinetLightColor === 'warm' ? '#FFD700' : cabinetLightColor === 'amber' ? '#FFBF00' : cabinetLightColor === 'blue' ? '#87CEEB' : '#FFFFFF'}`
                        }} data-id="element-1345"></div>
                          </div>
                          {/* Color swatches */}
                          <div className="flex space-x-2 mb-2" data-id="element-1346">
                            <button className={`h-6 w-10 rounded-sm text-xs flex items-center justify-center ${cabinetLightColor === 'white' ? 'ring-2 ring-blue-500 dark:ring-accent-blue' : ''}`} style={{
                          backgroundColor: '#FFFFFF',
                          color: '#000000'
                        }} onClick={() => {
                          setCabinetLightColor('white');
                          setShowCustomColorPicker(false);
                        }} data-id="element-1347">
                              White
                            </button>
                            <button className={`h-6 w-12 rounded-sm text-xs flex items-center justify-center ${cabinetLightColor === 'neutral' ? 'ring-2 ring-blue-500 dark:ring-accent-blue' : ''}`} style={{
                          backgroundColor: '#F5F5DC',
                          color: '#000000'
                        }} onClick={() => {
                          setCabinetLightColor('neutral');
                          setShowCustomColorPicker(false);
                        }} data-id="element-1348">
                              Neutral
                            </button>
                            <button className={`h-6 w-10 rounded-sm text-xs flex items-center justify-center ${cabinetLightColor === 'warm' ? 'ring-2 ring-blue-500 dark:ring-accent-blue' : ''}`} style={{
                          backgroundColor: '#FFD700',
                          color: '#000000'
                        }} onClick={() => {
                          setCabinetLightColor('warm');
                          setShowCustomColorPicker(false);
                        }} data-id="element-1349">
                              Warm
                            </button>
                            <button className={`h-6 w-12 rounded-sm text-xs flex items-center justify-center ${cabinetLightColor === 'amber' ? 'ring-2 ring-blue-500 dark:ring-accent-blue' : ''}`} style={{
                          backgroundColor: '#FFBF00',
                          color: '#000000'
                        }} onClick={() => {
                          setCabinetLightColor('amber');
                          setShowCustomColorPicker(false);
                        }} data-id="element-1350">
                              Amber
                            </button>
                            <button className={`h-6 w-10 rounded-sm text-xs flex items-center justify-center ${cabinetLightColor === 'blue' ? 'ring-2 ring-blue-500 dark:ring-accent-blue' : ''}`} style={{
                          backgroundColor: '#87CEEB',
                          color: '#000000'
                        }} onClick={() => {
                          setCabinetLightColor('blue');
                          setShowCustomColorPicker(false);
                        }} data-id="element-1351">
                              Blue
                            </button>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-1352">
                            <button className={`h-6 px-2 rounded-sm text-xs flex items-center justify-center bg-gray-200 hover:bg-gray-300 dark:bg-darkbg-primary dark:hover:bg-darkbg-border ${cabinetLightColor === 'custom' ? 'ring-2 ring-blue-500 dark:ring-accent-blue' : ''}`} onClick={() => {
                          setCabinetLightColor('custom');
                          setShowCustomColorPicker(!showCustomColorPicker);
                        }} data-id="element-1353">
                              Custom
                            </button>
                            {cabinetLightColor === 'custom' && showCustomColorPicker && <div className="ml-2 flex items-center" data-id="element-1354">
                                  <input type="color" value={customColor} onChange={e => setCustomColor(e.target.value)} className="h-6 w-10 cursor-pointer rounded-sm" data-id="element-1355" />
                                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1" data-id="element-1356">
                                    {customColor}
                                  </span>
                                </div>}
                          </div>
                        </div>
                        <div data-id="element-1357">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1358">
                            Intensity
                          </label>
                          <div className="flex items-center" data-id="element-1359">
                            <span className="text-xs text-gray-500 mr-2" data-id="element-1360">
                              Low
                            </span>
                            <div className="relative flex-1 h-4" data-id="element-1361">
                              <input type="range" min="0" max="100" value={cabinetIntensity} onChange={e => setCabinetIntensity(parseInt(e.target.value))} className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-darkbg-primary" data-id="element-1362" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2" data-id="element-1363">
                              High
                            </span>
                            <span className="text-xs text-gray-500 ml-2 w-8 text-right" data-id="element-1364">
                              {cabinetIntensity}%
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-1365">
                          <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-1366">
                            Neon Light
                          </span>
                          <div className="relative inline-block w-10 align-middle select-none" data-id="element-1367">
                            <input type="checkbox" id="toggle-upper-cabinets" checked={upperCabinetsOnly} onChange={() => setUpperCabinetsOnly(!upperCabinetsOnly)} className="sr-only" data-id="element-1368" />
                            <label htmlFor="toggle-upper-cabinets" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${upperCabinetsOnly ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1369">
                              <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${upperCabinetsOnly ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1370"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Reset Button */}
                    <div className="px-4 py-2 border-t border-gray-200 dark:border-darkbg-border mt-2" data-id="element-1371">
                      <button onClick={resetLightingToDefaults} className="w-full flex items-center justify-center px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1372">
                        <RefreshCwIcon className="h-4 w-4 mr-1.5" data-id="element-1373" />
                        <span data-id="element-1374">Reset to Default</span>
                      </button>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          <div className="flex items-center space-x-3" data-id="element-1375">
            {/* Export Button - MOVED TO THE RIGHT SIDE AND HIGHLIGHTED */}
            <div className="relative" data-id="element-1376">
              <button onClick={() => setShowExportMenu(!showExportMenu)} className="export-button flex items-center px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm dark:bg-accent-blue dark:hover:bg-blue-500" data-id="element-1377">
                <ShareIcon className="h-4 w-4 mr-1.5" data-id="element-1378" />
                <span data-id="element-1379">Export</span>
              </button>
              {showExportMenu && <div className="export-menu absolute left-0 mt-1 w-96 bg-white border border-gray-200 rounded-md shadow-lg z-50 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1380">
                  <div className="p-3 border-b border-gray-200 dark:border-darkbg-border" data-id="element-1381">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-1382">
                      Export Options
                    </h3>
                    {/* Toggle sliders */}
                    <div className="space-y-3" data-id="element-1383">
                      <div className="flex items-center justify-between" data-id="element-1384">
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1385">
                          Include Company Logo
                        </span>
                        <div className="relative inline-block w-10 align-middle select-none" data-id="element-1386">
                          <input type="checkbox" id="toggle-company-logo" checked={includeCompanyLogo} onChange={() => setIncludeCompanyLogo(!includeCompanyLogo)} className="sr-only" data-id="element-1387" />
                          <label htmlFor="toggle-company-logo" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${includeCompanyLogo ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1388">
                            <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${includeCompanyLogo ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1389"></span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-1390">
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1391">
                          Include Customer Details
                        </span>
                        <div className="relative inline-block w-10 align-middle select-none" data-id="element-1392">
                          <input type="checkbox" id="toggle-customer-details" checked={includeCustomerDetails} onChange={() => setIncludeCustomerDetails(!includeCustomerDetails)} className="sr-only" data-id="element-1393" />
                          <label htmlFor="toggle-customer-details" className={`block overflow-hidden h-5 rounded-full cursor-pointer ${includeCustomerDetails ? 'bg-blue-600 dark:bg-accent-blue' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1394">
                            <span className={`block h-5 w-5 rounded-full bg-white border border-gray-300 transform transition-transform duration-200 ease-in ${includeCustomerDetails ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1395"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Export options - grid layout */}
                  <div className="p-3" data-id="element-1396">
                    <div className="grid grid-cols-3 gap-4" data-id="element-1397">
                      {/* Main export options */}
                      {exportOptions.map(option => <div key={option.id} className="flex flex-col items-center" onClick={() => handleExportAction(option.id)} data-id="element-1398">
                          <button className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 dark:bg-darkbg-primary dark:hover:bg-darkbg-border" data-id="element-1399">
                            <option.icon className="h-5 w-5 text-gray-700 dark:text-gray-300" data-id="element-1400" />
                          </button>
                          <span className="text-xs text-gray-600 mt-1 dark:text-gray-400" data-id="element-1401">
                            {option.label}
                          </span>
                        </div>)}
                      {/* Additional export options - always visible */}
                      {moreExportOptions.map(option => <div key={option.id} className="flex flex-col items-center" onClick={() => handleExportAction(option.id)} data-id="element-1402">
                          <button className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 dark:bg-darkbg-primary dark:hover:bg-darkbg-border" data-id="element-1403">
                            <option.icon className="h-5 w-5 text-gray-700 dark:text-gray-300" data-id="element-1404" />
                          </button>
                          <span className="text-xs text-gray-600 mt-1 dark:text-gray-400" data-id="element-1405">
                            {option.label}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>}
            </div>
            {/* Customer profile icon - NEW */}
            <button onClick={() => handleAction('profile')} className="p-1.5 text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" title="Customer Profile" data-id="element-1406">
              <UserIcon className="h-4 w-4" data-id="element-1407" />
            </button>
            <button onClick={() => handleAction('export')} className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1408">
              <DownloadIcon className="h-4 w-4 mr-1.5" data-id="element-1409" />
              <span data-id="element-1410">Download</span>
            </button>
            <button onClick={() => handleAction('print')} className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1411">
              <PrinterIcon className="h-4 w-4 mr-1.5" data-id="element-1412" />
              <span data-id="element-1413">Print</span>
            </button>
            {/* Settings button with dropdown */}
            <div className="relative" data-id="element-1414">
              <button onClick={() => setShowSettingsMenu(!showSettingsMenu)} className="settings-button p-1.5 text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" title="Settings" data-id="element-1415"></button>
              {/* Settings dropdown menu */}
              {showSettingsMenu && <div className="settings-menu absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50 dark:bg-darkbg-secondary dark:border-darkbg-border" data-id="element-1416">
                  <div className="p-2 border-b border-gray-200 dark:border-darkbg-border" data-id="element-1417">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1418">
                      Settings
                    </h3>
                  </div>
                  <div className="py-1" data-id="element-1419">
                    {settingsOptions.map(option => <button key={option.id} onClick={() => handleAction(option.id)} className="flex items-start w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1420">
                        <option.icon className="h-4 w-4 mt-0.5 mr-3 text-gray-500 dark:text-gray-400" data-id="element-1421" />
                        <div data-id="element-1422">
                          <div className="font-medium" data-id="element-1423">{option.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1424">
                            {option.description}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      <div className="md:hidden" data-id="element-1425">
        <div className="flex items-center justify-between px-4 h-12" data-id="element-1426">
          <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="text-gray-700 dark:text-gray-300" data-id="element-1427">
            {showMobileMenu ? <XIcon className="h-5 w-5" data-id="element-1428" /> : <MenuIcon className="h-5 w-5" data-id="element-1429" />}
          </button>
          {/* Customer profile icon for mobile - NEW */}
          <button onClick={() => handleAction('profile')} className="p-1.5 text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1430">
            <UserIcon className="h-4 w-4" data-id="element-1431" />
          </button>
          {/* Dark/Light mode toggle on mobile */}
          <button onClick={onToggleDarkMode} className="p-1.5 text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1432">
            {isDarkMode ? <SunIcon className="h-4 w-4" data-id="element-1433" /> : <MoonIcon className="h-4 w-4" data-id="element-1434" />}
          </button>
          <button onClick={() => setShowSettingsMenu(!showSettingsMenu)} className="p-1.5 text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary" data-id="element-1435">
            <SettingsIcon className="h-4 w-4" data-id="element-1436" />
          </button>
        </div>
        {/* Mobile menu dropdown */}
        {showMobileMenu && <div className="px-4 py-3 space-y-2 border-t border-gray-200 bg-gray-50 dark:bg-darkbg-primary dark:border-darkbg-border" data-id="element-1437">
            <button onClick={() => setShowExportMenu(!showExportMenu)} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-secondary" data-id="element-1438">
              <ShareIcon className="h-4 w-4 mr-3" data-id="element-1439" />
              <span data-id="element-1440">Export</span>
            </button>
            <button onClick={() => handleAction('print')} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-secondary" data-id="element-1441">
              <PrinterIcon className="h-4 w-4 mr-3" data-id="element-1442" />
              <span data-id="element-1443">Print</span>
            </button>
            <div className="border-t border-gray-200 dark:border-darkbg-border pt-2" data-id="element-1444">
              {settingsOptions.map(option => <button key={option.id} onClick={() => handleAction(option.id)} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-secondary" data-id="element-1445">
                  <option.icon className="h-4 w-4 mr-3" data-id="element-1446" />
                  <span data-id="element-1447">{option.name}</span>
                </button>)}
              <button onClick={() => handleAction('help')} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-secondary" data-id="element-1448">
                <HelpCircleIcon className="h-4 w-4 mr-3" data-id="element-1449" />
                <span data-id="element-1450">Help</span>
              </button>
            </div>
          </div>}
        {/* Mobile settings menu */}
        {showSettingsMenu && !showMobileMenu && <div className="px-4 py-3 space-y-2 border-t border-gray-200 bg-gray-50 dark:bg-darkbg-primary dark:border-darkbg-border" data-id="element-1451">
            {settingsOptions.map(option => <button key={option.id} onClick={() => handleAction(option.id)} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded dark:text-gray-300 dark:hover:bg-darkbg-secondary" data-id="element-1452">
                <option.icon className="h-4 w-4 mr-3" data-id="element-1453" />
                <span data-id="element-1454">{option.name}</span>
              </button>)}
          </div>}
      </div>
    </div>;
}